import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button, StickyBottomActions } from "../../design-v2";
import {
  startEmailAuthentication,
  verifyEmailAuthentication,
} from "../../services/api";
import AuthLayout from "../AuthLayout";
import { getAuthenticatedDestination } from "../authRouting";
import {
  buildAuthEmailRecoveryPath,
  normalizeInternalReturnPath,
} from "../returnPath";
import OtpInput from "../components/OtpInput";
import useAuthBootstrap from "../useAuthBootstrap";
import { rememberLastUsedAuthProvider } from "../components/ProviderButton";

const OTP_COOLDOWN_SECONDS = 60;
const VERIFY_STATES = Object.freeze({
  DEFAULT: "default",
  INVALID: "invalid_code",
  EXPIRED: "expired_code",
  ATTEMPTS: "attempts_exceeded",
  VERIFYING: "verifying",
  REQUEST_RETRY: "request_retry",
  NETWORK: "network_error",
  RESEND_THROTTLED: "resend_throttled",
  RESEND_DELIVERY: "resend_delivery_failed",
  RESEND_NETWORK: "resend_network_error",
});

function formatCountdown(seconds) {
  const safe = Math.max(0, seconds);
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}

function stateMessage(state) {
  switch (state) {
    case VERIFY_STATES.INVALID:
      return "Incorrect code. Please try again.";
    case VERIFY_STATES.EXPIRED:
      return "This code has expired. Request a new one.";
    case VERIFY_STATES.ATTEMPTS:
      return "Too many attempts. Request a new code.";
    case VERIFY_STATES.NETWORK:
      return "We couldn't verify your code. Check your connection and try again.";
    case VERIFY_STATES.REQUEST_RETRY:
      return "We couldn't complete that request. Please try again.";
    case VERIFY_STATES.RESEND_THROTTLED:
      return "Too many requests. Try again in a few minutes.";
    case VERIFY_STATES.RESEND_DELIVERY:
      return "We couldn't send your code. Please try again.";
    case VERIFY_STATES.RESEND_NETWORK:
      return "We couldn't reach Lyft Zone. Check your connection and try again.";
    default:
      return "";
  }
}

export default function OtpVerifyScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const bootstrap = useAuthBootstrap();
  const email = typeof location.state?.email === "string" ? location.state.email : "";
  const next = normalizeInternalReturnPath(location.state?.next);
  const [code, setCode] = useState("");
  const [state, setState] = useState(VERIFY_STATES.DEFAULT);
  const [remaining, setRemaining] = useState(OTP_COOLDOWN_SECONDS);
  const [resendAnnouncement, setResendAnnouncement] = useState("");
  const [resending, setResending] = useState(false);
  const verificationInFlightRef = useRef(false);

  useEffect(() => {
    if (remaining <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setRemaining((current) => {
        const next = Math.max(0, current - 1);
        if (next === 0) {
          setResendAnnouncement("You can now request a new code.");
        }
        return next;
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [remaining]);

  if (bootstrap.status === "authenticated") {
    return (
      <Navigate
        to={getAuthenticatedDestination(bootstrap.onboarding, next)}
        replace
      />
    );
  }
  if (!email) {
    return <Navigate to={buildAuthEmailRecoveryPath(next || "/")} replace />;
  }

  const terminal = state === VERIFY_STATES.EXPIRED || state === VERIFY_STATES.ATTEMPTS;
  const busy = state === VERIFY_STATES.VERIFYING || resending;
  const message = stateMessage(state);

  const changeEmail = () => {
    navigate(buildAuthEmailRecoveryPath(next || "/"), { state: { email } });
  };

  const submit = async (eventOrCompleteCode) => {
    const completedCode = typeof eventOrCompleteCode === "string"
      ? eventOrCompleteCode
      : code;
    eventOrCompleteCode?.preventDefault?.();
    if (
      completedCode.length !== 6 ||
      terminal ||
      busy ||
      verificationInFlightRef.current
    ) {
      return;
    }
    verificationInFlightRef.current = true;
    setState(VERIFY_STATES.VERIFYING);
    try {
      await verifyEmailAuthentication({ email, code: completedCode });
      rememberLastUsedAuthProvider("email");
      setCode("");
      navigate("/auth/return", { replace: true, state: { next } });
    } catch (error) {
      if (error?.code === "OTP_INVALID") {
        setCode("");
        setState(VERIFY_STATES.INVALID);
      } else if (error?.code === "OTP_EXPIRED") {
        setState(VERIFY_STATES.EXPIRED);
      } else if (error?.code === "OTP_ATTEMPTS_EXCEEDED") {
        setState(VERIFY_STATES.ATTEMPTS);
      } else if (error?.code === "OTP_CONCURRENCY_RETRY") {
        setState(VERIFY_STATES.REQUEST_RETRY);
      } else {
        setState(VERIFY_STATES.NETWORK);
      }
    } finally {
      verificationInFlightRef.current = false;
    }
  };

  const resend = async () => {
    if (remaining > 0 || busy) {
      return;
    }
    setResending(true);
    setResendAnnouncement("");
    try {
      const result = await startEmailAuthentication(email);
      if (result?.devAutoVerified === true) {
        rememberLastUsedAuthProvider("email");
        setCode("");
        navigate("/auth/return", { replace: true, state: { next } });
        return;
      }
      setCode("");
      setState(VERIFY_STATES.DEFAULT);
      setRemaining(OTP_COOLDOWN_SECONDS);
    } catch (error) {
      if (error?.code === "OTP_RATE_LIMITED") {
        setState(VERIFY_STATES.RESEND_THROTTLED);
        setRemaining(OTP_COOLDOWN_SECONDS);
      } else if (error?.code === "EMAIL_DELIVERY_FAILED") {
        setState(VERIFY_STATES.RESEND_DELIVERY);
      } else if (error?.code === "OTP_CONCURRENCY_RETRY") {
        setState(VERIFY_STATES.REQUEST_RETRY);
      } else {
        setState(VERIFY_STATES.RESEND_NETWORK);
      }
    } finally {
      setResending(false);
    }
  };

  const countdown = formatCountdown(remaining);
  const requestNewLabel = remaining > 0
    ? `Request new code (${countdown})`
    : "Request new code";

  return (
    <AuthLayout onBack={changeEmail} backLabel="Change email" hasStickyActions>
      <form className="lz-auth-verify" onSubmit={submit}>
        <div className="lz-auth-verify__body">
          <div className="lz-auth-intro">
            <h1>VERIFY YOUR EMAIL.</h1>
            <p>Enter the 6-digit code sent to <strong>{email}</strong></p>
          </div>

          <OtpInput
            value={code}
            autoFocus
            onComplete={submit}
            onChange={(nextCode) => {
              setCode(nextCode);
              if (state === VERIFY_STATES.INVALID) {
                setState(VERIFY_STATES.DEFAULT);
              }
            }}
            error={state === VERIFY_STATES.INVALID || terminal}
            disabled={terminal || busy}
          />

          {message ? (
            <div className="lz-auth-alert" role="alert">{message}</div>
          ) : null}

          {!terminal ? (
            <div className="lz-auth-inline-actions">
              <Button
                variant="ghost"
                className="lz-auth-inline-actions__resend"
                onClick={resend}
                disabled={remaining > 0 || busy}
              >
                Resend code{remaining > 0 ? ` (${countdown})` : ""}
              </Button>
              <Button
                variant="ghost"
                className="lz-auth-inline-actions__change"
                onClick={changeEmail}
                disabled={busy}
              >
                Change email
              </Button>
            </div>
          ) : (
            <Button variant="ghost" onClick={changeEmail} disabled={busy}>
              Change email
            </Button>
          )}

          <span
            className="lz-auth-screen-reader-status"
            aria-live="polite"
            aria-atomic="true"
          >
            {resendAnnouncement}
          </span>
        </div>

        <StickyBottomActions>
          {terminal ? (
            <Button
              size="lg"
              className="lz-auth-primary-action"
              onClick={resend}
              disabled={remaining > 0}
              isLoading={resending}
              loadingLabel="Sending..."
            >
              {requestNewLabel}
            </Button>
          ) : (
            <Button
              type="submit"
              size="lg"
              className="lz-auth-primary-action"
              disabled={code.length !== 6}
              isLoading={state === VERIFY_STATES.VERIFYING}
              loadingLabel="Verifying..."
            >
              Verify &amp; Continue
            </Button>
          )}
        </StickyBottomActions>
      </form>
    </AuthLayout>
  );
}

export {
  OTP_COOLDOWN_SECONDS,
  VERIFY_STATES,
  formatCountdown,
};
